import React, { useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Recycle, ShieldCheck, Cpu, PackageCheck, BarChart4, ChevronLeft, ChevronRight } from "lucide-react";

export default function ChiSiamo() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const carouselMedia = [
    { type: 'image', src: "/media/chatgpt-image-19-apr-2026-08-33-28-ionela-1.jpg" },
    { type: 'video', src: "/media/whatsapp-video-2026-04-19-at-19.24.11-film-1.mp4" },
    { type: 'image', src: "/media/chatgpt-image-19-apr-2026-19-32-10-ionela-2.jpg" },
    { type: 'video', src: "/media/whatsapp-video-2026-04-19-at-19.24.11-film-2.mp4" },
    { type: 'image', src: "/media/chatgpt-image-19-apr-2026-19-37-26-ionela-3.jpg" },
    { type: 'video', src: "/media/whatsapp-video-2026-04-19-at-19.24.11-film-3.mp4" },
    { type: 'image', src: "/media/chatgpt-image-19-apr-2026-19-41-12-ionela-4.jpg" },
    { type: 'video', src: "/media/whatsapp-video-2026-04-19-at-19.24.13-film-4.mp4" },
    { type: 'image', src: "/media/chatgpt-image-19-apr-2026-19-46-00-ionela-5.jpg" },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? carouselMedia.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === carouselMedia.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="w-full bg-white text-black min-h-screen">
      {/* HERO SECTION */}
      <section className="relative pt-40 md:pt-64 pb-40 px-6 lg:px-12 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
           <img src="/media/foto-hala-1.jpg" alt="Izosmart Sede" className="w-full h-full object-cover object-[center_60%] opacity-90" />
           <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
        </div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-[7rem] font-bold tracking-tighter mb-8 uppercase leading-[0.9]"
          >
            Le radici del<br/><span className="text-[#7dba00]">nostro futuro.</span>
          </motion.h1>
        </div>
      </section>

      {/* MEDIA CAROUSEL SECTION */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto max-w-6xl">
          <motion.h2 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="text-4xl md:text-6xl font-bold tracking-tighter mb-16 text-center uppercase"
          >
            La Nostra Fabbrica
          </motion.h2>

          <div className="relative w-full aspect-[4/3] md:aspect-[21/9] bg-black rounded-2xl overflow-hidden shadow-2xl flex items-center justify-center group">
            {carouselMedia.map((media, i) => (
              <div 
                key={media.src} 
                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${i === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                {media.type === 'image' ? (
                  <img src={media.src} alt={`La fabbrica Izosmart ad Avio – foto ${i + 1}`} className="w-full h-full object-contain bg-white" />
                ) : (
                  <video src={media.src} className="w-full h-full object-contain bg-white" autoPlay muted loop playsInline controls />
                )}
              </div>
            ))}

            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 z-20 bg-black/50 text-white p-3 rounded-full hover:bg-[#7dba00] transition-colors"
            >
              <ChevronLeft size={32} />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 z-20 bg-black/50 text-white p-3 rounded-full hover:bg-[#7dba00] transition-colors"
            >
              <ChevronRight size={32} />
            </button>
            
            {/* Dots */}
            <div className="absolute bottom-4 z-20 flex gap-2">
              {carouselMedia.map((media, i) => (
                <button 
                  key={media.src} 
                  onClick={() => setCurrentIndex(i)}
                  className={`w-3 h-3 rounded-full transition-colors ${i === currentIndex ? 'bg-[#7dba00]' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* I NOSTRI VALORI E OBIETTIVI */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl space-y-32">
          
          {/* 1. La nostra missione */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="flex flex-col md:flex-row gap-12 lg:gap-20">
            <div className="w-full md:w-1/3">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-tight text-[#7dba00]">01.</h2>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tighter mt-4">La nostra missione</h3>
            </div>
            <div className="w-full md:w-2/3 text-lg md:text-xl text-gray-600 font-light leading-relaxed space-y-6">
              <p>La nostra missione è chiara e ambiziosa: diventare uno dei principali produttori di serramenti di alta qualità sul mercato italiano. Non ci limitiamo a offrire prodotti, ma vogliamo creare valore concreto per i nostri clienti e partner, attraverso soluzioni affidabili, durature e tecnologicamente avanzate.</p>
              <p>Crediamo che il mercato richieda sempre più professionalità, continuità e attenzione reale alla qualità, non solo competitività sul prezzo. Per questo investiamo costantemente in tecnologia, organizzazione e formazione del personale.</p>
              <p>Il nostro processo produttivo è progettato per garantire standard elevati, precisione e tempi di consegna affidabili. Ogni fase è monitorata con attenzione, perché riteniamo che la qualità non sia un’opzione, ma una responsabilità.</p>
              <p>Il nostro obiettivo non è una crescita rapida e disordinata, ma uno sviluppo solido, basato su relazioni di fiducia e risultati concreti. Vogliamo essere riconosciuti come un partner serio e competente, capace di offrire prodotti che mantengono le loro prestazioni nel tempo.</p>
              <p>Per noi, ogni progetto rappresenta un impegno diretto verso il cliente e un passo avanti nella costruzione di una reputazione duratura sul mercato italiano.</p>
            </div>
          </motion.div>

          {/* 2. Perché Schüco? Perché Izosmart? */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="flex flex-col md:flex-row gap-12 lg:gap-20">
            <div className="w-full md:w-1/3">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-tight text-[#7dba00]">02.</h2>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tighter mt-4">Perché Schüco?<br/>Perché Izosmart?</h3>
            </div>
            <div className="w-full md:w-2/3 text-lg md:text-xl text-gray-600 font-light leading-relaxed space-y-6">
              <p>Abbiamo scelto di lavorare con Schüco perché rappresenta uno dei riferimenti più solidi nel settore dei sistemi per serramenti in Europa. Il marchio è sinonimo di innovazione, qualità e affidabilità, caratteristiche fondamentali per offrire prodotti all’altezza delle aspettative del mercato moderno.</p>
              <p>Schüco non è solo un fornitore di profili, ma un partner tecnologico che garantisce standard elevati in termini di prestazioni termiche, sicurezza e design.</p>
              <p>Izosmart integra questi sistemi con un approccio produttivo orientato alla precisione e al controllo della qualità. Non ci limitiamo all’assemblaggio, ma gestiamo con attenzione ogni fase del processo, dalla selezione dei materiali fino al prodotto finito.</p>
              <p>Questa combinazione ci permette di offrire serramenti che rispondono in modo concreto alle esigenze di comfort, efficienza energetica e durata nel tempo.</p>
              <p>Scegliere Izosmart significa affidarsi a un partner che non accetta compromessi sulla qualità e che costruisce ogni prodotto con responsabilità e competenza. Il nostro obiettivo è offrire soluzioni affidabili oggi e sostenibili nel lungo periodo.</p>
            </div>
          </motion.div>

          {/* 3. Dove siamo */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="flex flex-col md:flex-row gap-12 lg:gap-20">
            <div className="w-full md:w-1/3">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-tight text-[#7dba00]">03.</h2>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tighter mt-4">Dove siamo</h3>
            </div>
            <div className="w-full md:w-2/3 text-lg md:text-xl text-gray-600 font-light leading-relaxed space-y-6">
              <p>La nostra sede produttiva si trova ad Avio, frazione Sabbionara, in Viale al Parco 3. Si tratta di una posizione strategica che ci consente di operare in modo efficiente su gran parte del territorio del Nord Italia.</p>
              <p>Abbiamo scelto questa localizzazione per essere vicini ai nostri partner e per garantire tempi di risposta rapidi e un servizio puntuale.</p>
              <p>Oltre alla presenza fisica, siamo attivi anche online, perché riteniamo fondamentale offrire canali di comunicazione semplici e immediati. È possibile trovarci sul nostro sito ufficiale, così come sulle principali piattaforme social: Facebook, Instagram e LinkedIn, dove condividiamo aggiornamenti, progetti e informazioni utili.</p>
              <p>Per qualsiasi richiesta, siamo disponibili via email all’indirizzo <strong>info@izosmart.com</strong> oppure telefonicamente al numero <strong>+39 350 9500280</strong>.</p>
              <p>Crediamo in una comunicazione diretta, trasparente ed efficiente, perché una buona collaborazione inizia sempre da un contatto chiaro e accessibile.</p>
            </div>
          </motion.div>

          {/* 4. Partner */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="flex flex-col md:flex-row gap-12 lg:gap-20">
            <div className="w-full md:w-1/3">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-tight text-[#7dba00]">04.</h2>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tighter mt-4">Partner</h3>
            </div>
            <div className="w-full md:w-2/3 text-lg md:text-xl text-gray-600 font-light leading-relaxed space-y-6">
              <p>Il nostro obiettivo è sviluppare una rete di distribuzione solida e ben organizzata in tutta Italia. Non puntiamo a una crescita casuale, ma alla costruzione di partnership strutturate e durature.</p>
              <p>Vogliamo collaborare con agenti e professionisti locali in grado di sviluppare e gestire una rete di rivenditori autorizzati nelle diverse province italiane. Questo modello ci consente di essere presenti sul territorio in modo efficace, mantenendo allo stesso tempo standard elevati di qualità e servizio.</p>
              <p>I nostri partner non sono semplici intermediari, ma rappresentano direttamente il valore del marchio Izosmart sul mercato. Per questo selezioniamo collaboratori che condividono i nostri principi: serietà, professionalità e orientamento al cliente.</p>
              <p>Offriamo supporto tecnico, consulenza e prodotti competitivi, creando le condizioni per una crescita comune.</p>
              <p>Il nostro obiettivo finale è costruire una presenza nazionale concreta, basata su relazioni di fiducia e risultati verificabili nel tempo.</p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* SOSTENIBILITA */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-black text-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mb-20 text-center">
            <span className="text-[#7dba00] font-bold uppercase tracking-[0.2em] text-sm mb-4 block">Il Nostro Impegno</span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase">Cultura Produttiva Sostenibile</h2>
          </motion.div>

          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
            className="grid md:grid-cols-3 gap-12 lg:gap-16"
          >
            <motion.div variants={fadeInUp} className="bg-white/5 p-10 border border-white/10 hover:border-[#7dba00] transition-colors group">
              <Recycle size={40} className="text-[#7dba00] mb-6" />
              <h3 className="text-2xl font-bold mb-4 group-hover:text-[#7dba00] transition-colors">PVC Rigenerato</h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Utilizziamo profili in PVC senza piombo e ricicliamo attivamente i rifiuti di PVC e vetro. La materia proviene da fonti riciclate ed è riutilizzabile.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white/5 p-10 border border-white/10 hover:border-[#7dba00] transition-colors group">
              <Leaf size={40} className="text-[#7dba00] mb-6" />
              <h3 className="text-2xl font-bold mb-4 group-hover:text-[#7dba00] transition-colors">Riduzione CO2</h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Attuiamo politiche di riqualificazione interna per ridurre al minimo l’impatto ambientale del nostro intero sistema produttivo logistico.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white/5 p-10 border border-white/10 hover:border-[#7dba00] transition-colors group">
              <ShieldCheck size={40} className="text-[#7dba00] mb-6" />
              <h3 className="text-2xl font-bold mb-4 group-hover:text-[#7dba00] transition-colors">Certificazioni</h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Desideriamo crescere garantendo qualità assoluta. Per questo rispettiamo i più severi standard europei sulla sicurezza e sostenibilità.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PRODUZIONE */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="w-full md:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-8 leading-tight">
                Produzione automatizzata e controllo costante.
              </h2>
              <p className="text-xl text-gray-600 font-light mb-10 leading-relaxed">
                Il nostro impianto produttivo è all'avanguardia ed è organizzato per ottenere una schedulazione dettagliata di tutta l’operatività, raggiungendo così date di consegna precise e garantendo un <strong>Lead Time eccellente</strong>.
              </p>
              <ul className="space-y-6 text-lg font-light text-gray-800">
                <li className="flex items-center gap-4"><Cpu className="text-[#7dba00]" /> Schedulazione avanzata a capacità finita</li>
                <li className="flex items-center gap-4"><PackageCheck className="text-[#7dba00]" /> Controllo costante delle giacenze a magazzino</li>
                <li className="flex items-center gap-4"><BarChart4 className="text-[#7dba00]" /> Ottimizzazione dei consumi energetici</li>
              </ul>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
              className="w-full md:w-1/2 flex justify-center items-center overflow-hidden"
            >
              <img 
                src="/media/chatgpt-image-19-apr-2026-08-33-28-ionela-1.jpg" 
                alt="Produzione Izosmart" 
                className="max-w-full max-h-[600px] object-contain hover:scale-105 transition-transform duration-1000 ease-out" 
              />
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}
